import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { useCanAccessRoute } from 'src/hooks/use-permissions';

import { getRedirectRoute } from 'src/utils/auto-redirect';

import { useAuthContext } from 'src/auth/hooks';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

/**
 * Componente para proteger rutas basado en permisos del usuario
 * ⚠️ MODO ULTRA-RESTRICTIVO: Solo master ve todo, el resto SOLO lo que tiene permisos
 * Redirige o muestra mensaje de acceso denegado si el usuario no tiene permisos
 */
const RouteGuard = ({
  children,
  route,
  fallbackRoute = '/medicore',
  showFallback = true,
  requireAuth = true,
  useSmartRedirect = true,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { user } = useAuthContext();
  const canAccess = useCanAccessRoute(route);

  useEffect(() => {
    if (!requireAuth) return;

    if (!canAccess && !showFallback) {
      if (useSmartRedirect) {
        const smartRedirectRoute = getRedirectRoute(user);
        router.push(smartRedirectRoute);
      } else {
        router.push(fallbackRoute);
      }
    }
  }, [canAccess, showFallback, fallbackRoute, router, requireAuth, useSmartRedirect, user, route]);

  if (!requireAuth) {
    return children;
  }

  if (canAccess) {
    return children;
  }

  if (!showFallback) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        px: 3,
        bgcolor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          maxWidth: 480,
          mx: 'auto',
        }}
      >
        <Box
          component="img"
          src="/assets/illustrations/illustration_403.svg"
          alt="Acceso denegado"
          sx={{
            height: 240,
            mx: 'auto',
            my: { xs: 5, sm: 8 },
            objectFit: 'contain',
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />

        <Typography variant="h3" sx={{ mb: 2 }}>
          Acceso Denegado
        </Typography>

        <Typography sx={{ color: 'text.secondary', mb: 4 }}>
          Lo sentimos, no tienes permisos para acceder a esta página.
          <br />
          Contacta al administrador del sistema si crees que esto es un error.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
          }}
        >
          <Button
            size="large"
            variant="contained"
            onClick={() => router.push(fallbackRoute)}
            startIcon={<Iconify icon="eva:home-fill" />}
          >
            Ir al Inicio
          </Button>

          <Button
            size="large"
            onClick={() => router.back()}
            startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
          >
            Regresar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

RouteGuard.propTypes = {
  children: PropTypes.node.isRequired,
  route: PropTypes.string.isRequired,
  fallbackRoute: PropTypes.string,
  showFallback: PropTypes.bool,
  requireAuth: PropTypes.bool,
  useSmartRedirect: PropTypes.bool,
};

export default RouteGuard;

// ----------------------------------------------------------------------

/**
 * HOC (Higher-Order Component) para proteger rutas
 * @param {string} route - Ruta que requiere protección
 * @param {Object} options - Opciones de configuración
 * @returns {Function} - Componente envuelto con protección
 */
export const withRouteGuard =
  (route, options = {}) =>
  (WrappedComponent) => {
    const GuardedComponent = (props) => (
      <RouteGuard route={route} {...options}>
        <WrappedComponent {...props} />
      </RouteGuard>
    );

    GuardedComponent.displayName = `withRouteGuard(${WrappedComponent.displayName || WrappedComponent.name})`;

    return GuardedComponent;
  };
